import React, { useState, useEffect, RefObject, useRef } from 'react';
import styled from '@emotion/styled';
import { Flex, Box } from 'reflexbox';
// import { Link } from '../../../interfaces';
import { CourseTypeItem } from './CourseTypeItem/index';
import { NavItem } from '../NavItem/index';
import { LinkProps } from '../../../../containers/Link/types';
import { MARKETING_PAGE_BREAKPOINT_DESKTOP } from '../../../Style/breakpoints';
import { RouteAction } from '../../../../../ReduxStorage/routeActions';

import ShortCourseIcon from './images/CourseTypes-icon1.svg';
import OpenCredIcon from './images/CourseTypes-icon2.svg';
import QualificationsIcon from './images/CourseTypes-icon3.svg';
import { MARKETING_PAGE_TOP_BAR_Z_INDEX } from '../../../Style/zindex';

interface SubNavLinkProps extends LinkProps {
  name: string;
  to: RouteAction;
}

interface DropdownProps {
  name: string;
  subnav?: SubNavLinkProps[];
  viewallbutton?: string;
  makeCourseFilterEvent?: (name: string, values: string[]) => void;
}

interface StyledNavItemProps {
  opened: boolean;
}

const SubNav = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;

  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    display: inline-block;
    z-index: 5;
    @-webkit-keyframes slide-bottom {
      0% {
        -webkit-transform: translateY(-50px);
        transform: translateY(-50px);
        opacity: 0;
      }
      100% {
        -webkit-transform: translateY(0);
        transform: translateY(0);
        opacity: 100;
      }
    }
    @keyframes slide-bottom {
      0% {
        -webkit-transform: translateY(-50px);
        transform: translateY(-50px);
        opacity: 0;
      }
      100% {
        -webkit-transform: translateY(0);
        transform: translateY(0);
        opacity: 100;
      }
    }
  }
`;

const SubnavContent = styled.div`
  cursor: auto;
  position: relative;
  background: transparent;
  display: block;
  margin: 0;
  padding: 0;
  z-index: ${MARKETING_PAGE_TOP_BAR_Z_INDEX + 1};
  left: 0;
  right: 0;
  top: 0;

  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    top: 88px;
    position: absolute;
    animation: slide-bottom 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
  }
`;

const ContentItemContainer = styled(Flex)`
  margin-top: 0;
  background: #ffffff;
  box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.15);
  padding: 8px;
  flex-direction: column;
  justify-content: space-around;
  top: 0;
  left: 0;
  width: 100%;

  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    flex-direction: row;
    padding: 30px 50px;
`;

const IconContainer = styled(Box)`
  width: 81px;
  height: 81px;
  fill: none;
`;

const StyledNavItem = styled(NavItem)<StyledNavItemProps>((props) => ({
  position: 'relative',

  ...(props.opened && {
    color: '#ffffff',
    background: '#cf1b53',

    '.chevron-down': {
      transform: 'rotate(180deg)',
    },

    [`@media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP})`]: {
      color: '#cf1b53',
      background: 'transparent',
    },
  }),
}));

// TODO: we can refector this part out into a resuable component for all
// top bar dropdown that takes in content as children prop
const CourseDropdownContent = React.memo(
  ({
    setDropDownOpened,
    makeCourseFilterEvent,
  }: {
    setDropDownOpened: (opened: boolean) => void;
    makeCourseFilterEvent?: (name: string, values: string[]) => void;
  }) => {
    const dropDownEl: RefObject<HTMLInputElement> = useRef(null);

    const onKeyPress = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setDropDownOpened(false);
      }
    };

    const onWindowClick = (event: MouseEvent | TouchEvent): void => {
      const dropdownElement = dropDownEl.current;

      if (
        event.target &&
        dropdownElement &&
        event.target !== dropdownElement &&
        !dropdownElement.contains(event.target as Node)
      ) {
        // stop onWindowClick being triggered twice by click and touchstart
        event.stopPropagation();
        event.preventDefault();

        setDropDownOpened(false);
      }
    };

    const onCourseTypeItemClicked = (courseTypes: string[]): void => {
      if (makeCourseFilterEvent) {
        makeCourseFilterEvent('courseTypes', courseTypes);
      }
    };

    useEffect(() => {
      // add listens after mounted
      window.addEventListener('click', onWindowClick);
      window.addEventListener('touchend', onWindowClick);
      window.addEventListener('keydown', onKeyPress);

      return () => {
        // remove listens before unmount
        window.removeEventListener('click', onWindowClick);
        window.removeEventListener('touchend', onWindowClick);
        window.removeEventListener('keydown', onKeyPress);
      };
    }, []);

    return (
      <SubnavContent ref={dropDownEl} className="subnav-content">
        <ContentItemContainer>
          <CourseTypeItem
            icon={
              <IconContainer>
                <img
                  src={ShortCourseIcon}
                  alt=""
                  className="short-course-icon"
                />
              </IconContainer>
            }
            header="Short courses"
            description="Flexible and short online courses, designed for exploring a new subject, trying out a new institution or acquiring new skills."
            link={{
              title: 'View short courses',
              to: {
                type: 'ROUTE_COURSES',
                query: {
                  filters: `{"sortBy":["popularity"],"courseTypes":["SHORT_COURSE"]}`,
                },
              },
            }}
            onClick={() => {
              onCourseTypeItemClicked(['SHORT_COURSE']);
            }}
          />
          <CourseTypeItem
            icon={
              <IconContainer>
                <img src={OpenCredIcon} alt="" className="short-course-icon" />
              </IconContainer>
            }
            header="OpenCreds"
            description="Learn new skills with practical, stackable online courses aligned to Australia’s first micro-credentialing framework for lifelong learning."
            link={{
              title: 'View OpenCreds',
              to: {
                type: 'ROUTE_COURSES',
                query: {
                  filters: `{"sortBy":["popularity"],"courseTypes":["OPENCRED"]}`,
                },
              },
            }}
            onClick={() => {
              onCourseTypeItemClicked(['OPENCRED']);
            }}
          />
          <CourseTypeItem
            icon={
              <IconContainer>
                <img
                  src={QualificationsIcon}
                  alt=""
                  className="short-course-icon"
                />
              </IconContainer>
            }
            header="Qualifications"
            description="Stack your OpenCreds to receive credit towards industry-relevant, engaging online qualifications from universities and colleges."
            link={{
              title: 'View qualifications',
              to: {
                type: 'ROUTE_COURSES',
                query: {
                  filters: `{"sortBy":["popularity"],"courseTypes":["QUALIFICATION"]}`,
                },
              },
            }}
            onClick={() => {
              onCourseTypeItemClicked(['QUALIFICATION']);
            }}
          />
        </ContentItemContainer>
      </SubnavContent>
    );
  }
);

export const CourseDropdown = React.memo(
  ({ name, makeCourseFilterEvent }: DropdownProps) => {
    const [dropDownOpened, setDropDownOpened] = useState<boolean>(false);

    const onNavItemClicked = (): void => {
      setDropDownOpened(!dropDownOpened);
    };

    return (
      <SubNav>
        <StyledNavItem
          name={name}
          onClick={onNavItemClicked}
          opened={dropDownOpened}
        />
        {dropDownOpened && (
          <CourseDropdownContent
            setDropDownOpened={setDropDownOpened}
            makeCourseFilterEvent={makeCourseFilterEvent}
          />
        )}
      </SubNav>
    );
  }
);
